import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Avatar,
  Grid,
  IconButton,
  Divider,
  OutlinedInput,
  Select,
  Chip,
  FormControlLabel,
  Switch,
  type SelectChangeEvent,
  CircularProgress,
  Alert,
} from "@mui/material";
import { CloudUpload, Edit, Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import CustomTextField from "../../components/common/Textfield";
import type { User } from "./types/User";
import Button from "../../components/common/Button";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../../service/Api";
import { toast } from "react-toastify";

const userRoles = ["Admin", "Sales", "Dev", "Operations", "Finance", "Support"];

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (userData: any) => void;
}

export default function EditUserDialog({
  open,
  onClose,
  onSave,
}: EditUserDialogProps) {
  const { id } = useParams<{ id: string }>(); // Extract user ID from URL
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    userRole: [] as string[],
    status: "Active",
  });
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user data by ID when dialog opens
  useEffect(() => {
    if (open && id) {
      setLoading(true);
      apiService
        .get<{
          id: string;
          email: string;
          username: string;
          first_name: string;
          last_name: string;
          phone_number: string | null;
          role: string;
          is_active: boolean;
          is_2fa_enabled: boolean;
          last_login: string | null;
          groups: string[];
          company: string | null;
        }>(`/api/authentication/users/${id}/`)
        .then((response) => {
          const user = response.data;
          setFormData({
            fullName:
              `${user.first_name} ${user.last_name}`.trim() || user.username,
            email: user.email,
            phoneNumber: user.phone_number || "",
            userRole: user.groups,
            status: user.is_active ? "Active" : "Suspended",
          });
          setProfileImage(
            user.first_name?.[0]?.toUpperCase() ||
              user.username[0].toUpperCase()
          );
          setError(null);
        })
        .catch((err: any) => {
          let errorMessage = "Failed to load user data. Please try again.";
          if (err.response?.status === 401) {
            errorMessage = "Unauthorized: Please log in again.";
            navigate("/login");
          } else if (err.response?.data?.detail) {
            errorMessage = err.response.data.detail;
          }
          setError(errorMessage);
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 5000,
            theme: "light",
          });
          console.error("Fetch user error:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, id, navigate]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for the field
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      userRole: typeof value === "string" ? value.split(",") : value,
    }));
    setErrors((prev) => ({ ...prev, userRole: "" }));
  };

  const handleStatusToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked ? "Active" : "Suspended",
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        setErrors((prev) => ({
          ...prev,
          profileImage: "Image size must be under 2MB",
        }));
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target?.result as string);
        setErrors((prev) => ({ ...prev, profileImage: "" }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName) {
      newErrors.fullName = "Full name is required";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (formData.userRole.length === 0) {
      newErrors.userRole = "At least one role is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveChanges = () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Split fullName into first_name and last_name
    const [firstName, ...lastNameParts] = formData.fullName.split(" ");
    const lastName = lastNameParts.join(" ");
    const userData = {
      first_name: firstName || "",
      last_name: lastName || "",
      email: formData.email,
      phone_number: formData.phoneNumber || null,
      roles: formData.userRole, // Maps to groups in backend
      is_active: formData.status === "Active",
      profile_image: profileImage,
      id,
    };
    onSave(userData);
    setIsSubmitting(false);
  };

  const handleChangePassword = () => {
    console.log("Change password clicked");
    // Implement password change logic (e.g., navigate to a password reset page or open a modal)
  };

  const handleClose = () => {
    // Reset form data when closing
    setFormData({
      fullName: "",
      email: "",
      phoneNumber: "",
      userRole: [],
      status: "Active",
    });
    setProfileImage(null);
    setErrors({});
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "600px",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" component="div">
            Edit User
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress
              sx={{ color: reduxTheme.primary_colors?.[400] || "#614438" }}
            />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button
              onClick={() => {
                setError(null);
              }}
              sx={{ ml: 2 }}
            >
              Retry
            </Button>
          </Alert>
        ) : (
          <>
            {/* Profile Photo Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
              }}
            >
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  mb: 2,
                  backgroundColor: "#e0e0e0",
                  border: "4px solid #f0f0f0",
                  fontSize: "2rem",
                }}
                src={profileImage || undefined}
              >
                {!profileImage && (
                  <CloudUpload sx={{ fontSize: 40, color: "#999" }} />
                )}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="photo-upload"
                type="file"
                onChange={handleImageUpload}
              />
              <label htmlFor="photo-upload">
                <Button
                  variant="contained"
                  component="span"
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                  }}
                >
                  Upload Photo
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary">
                PNG, JPG up to 2MB
              </Typography>
              {errors.profileImage && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.profileImage}
                </Typography>
              )}
            </Box>
            {/* Form Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Full Name */}
              <CustomTextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter full name"
                required
                variant="outlined"
                size="small"
                error={!!errors.fullName}
                helperText={errors.fullName}
              />
              {/* Email Address */}
              <CustomTextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="name@company.com"
                required
                variant="outlined"
                size="small"
                error={!!errors.email}
                helperText={errors.email}
              />
              {/* Phone Number */}
              <CustomTextField
                fullWidth
                label="Phone Number (Optional)"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="+1 (555) 000-0000"
                variant="outlined"
                size="small"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
              />
              {/* Password Section */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Password
                  </Typography>
                  <Button
                    variant="text"
                    startIcon={<Edit />}
                    onClick={handleChangePassword}
                    size="small"
                    sx={{
                      color: reduxTheme.primary_colors[500],
                      textTransform: "none",
                      fontSize: "12px",
                    }}
                  >
                    Change Password
                  </Button>
                </Box>
                <Divider />
              </Box>
              {/* User Role */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Role Assignment
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth error={!!errors.userRole}>
                  <InputLabel id="roles-label">Roles</InputLabel>
                  <Select
                    labelId="roles-label"
                    multiple
                    value={formData.userRole}
                    onChange={handleRoleChange}
                    disabled={isSubmitting}
                    input={<OutlinedInput label="Roles" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {userRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.userRole && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 2 }}
                    >
                      {errors.userRole}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {/* Account Status */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Account Status
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === "Active"}
                      onChange={handleStatusToggle}
                      disabled={isSubmitting}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>Account Status:</Typography>
                      <Chip
                        label={formData.status}
                        color={
                          formData.status === "Active" ? "success" : "warning"
                        }
                        size="small"
                      />
                    </Box>
                  }
                />
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: "none" }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveChanges}
          sx={{
            backgroundColor: "primary.main",
          }}
          disabled={isSubmitting || loading}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
